import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

/**
 * Client Autonomy — PROCESS-level actions only (start / close).
 * Candidate management (add, assign, preselect) lives in the single
 * canonical "Candidatos" roster card (Founder UAT P0-1 consolidation).
 */
export function ClientSelfServiceOps({ proc, onChanged }) {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [closeReason, setCloseReason] = useState('');
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/recruitment-processes/${proc.id}/approve`);
      toast.success(L('Proceso iniciado — ya puedes agregar candidatos.', 'Process started — you can now add candidates.'));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const closeProc = async () => {
    if (closeReason.trim().length < 3) {
      toast.error(L('Indique el motivo de cierre.', 'Provide a closing reason.')); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/recruitment-processes/${proc.id}/close`, { reason: closeReason.trim() });
      toast.success(L('Proceso cerrado.', 'Process closed.'));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (proc.closed_at || proc.stage >= 11) return null;

  return (
    <Card className="p-5 shadow-card space-y-4" data-testid="tc-self-service-ops">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">{L('Acciones del proceso', 'Process actions')}</h3>
        <Badge variant="outline" className="text-[10px]">{L('Autogestión', 'Autonomous')}</Badge>
      </div>

      {proc.stage === 2 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 flex items-center justify-between gap-2">
          <p className="text-sm">{L('Perfil del puesto completado. Inicia el proceso cuando estés listo — no requiere aprobación de CTH.',
            'Position profile completed. Start the process whenever you are ready — no CTH approval required.')}</p>
          <Button size="sm" onClick={approve} disabled={busy} data-testid="tc-self-approve">
            <CheckCircle2 className="h-4 w-4 mr-1" /> {L('Iniciar proceso', 'Start process')}
          </Button>
        </div>
      )}

      {proc.stage >= 3 && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-64">
            <Label className="text-xs">{L('Cerrar proceso — motivo', 'Close process — reason')}</Label>
            <Input value={closeReason} onChange={(e) => setCloseReason(e.target.value)} data-testid="tc-close-reason" />
          </div>
          <Button size="sm" variant="destructive" onClick={closeProc} disabled={busy} data-testid="tc-close-btn">
            <XCircle className="h-4 w-4 mr-1" /> {L('Cerrar proceso', 'Close process')}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default ClientSelfServiceOps;
